import { useMemo } from "react";

import {
  normalizeSearchPanelState,
  useCachedSearchReferenceLabels,
  type SearchReferenceLabels,
  type SearchScope,
} from "@tsmono/inspect-components/transcript-search";

import { useStore } from "../../../../state/store";

import {
  getInspectSearchPanelStateKey,
  useInspectSearchApi,
  type InspectSearchContext,
} from "./inspectSearchAdapters";

/**
 * Derives transcript cite labels from the active search for `scope`, reading
 * the same panel state the SearchPanel writes to. Returns undefined when the
 * sample isn't searchable or no search has run, so callers can merge it into
 * the transcript context without extra gating.
 */
export const useInspectSearchReferenceLabels = ({
  scope,
  context,
}: {
  scope: SearchScope;
  context: InspectSearchContext | null;
}): SearchReferenceLabels | undefined => {
  const logFile = context?.logFile;
  const transcriptId = context?.transcriptId;

  const key = useMemo(
    () =>
      logFile && transcriptId
        ? getInspectSearchPanelStateKey({ scope, logFile, transcriptId })
        : null,
    [scope, logFile, transcriptId]
  );

  const stored = useStore((s) => (key ? s.search.panelStates[key] : undefined));
  const state = useMemo(() => normalizeSearchPanelState(stored), [stored]);
  const searchId = state.searches[state.searchType].searchId;

  const api = useInspectSearchApi(logFile ?? "", transcriptId ?? "");

  return useCachedSearchReferenceLabels({
    api,
    scope,
    searchId: context ? searchId : null,
  });
};
